<template>
	<span class="input-file-wrapper">
		<app-button
			v-if="button"
			:color="buttonColor"
			:icon="icon"
			:outlined="outlined"
			:tile="tile"
			:small="small"
			:large="large"
			:table="table"
			@click="$refs.file.click()"
		>
			{{label}}
		</app-button>
		<div
			v-if="image"
			:class="{
				dropper: true,
				dropperDark: imageDark,
			}"
			@dragover.prevent
			@drop.prevent="onNativeChange($event.dataTransfer.files)"
		>
			<div v-if="label" @click="$refs.file.click()">{{label}}</div>
			<span v-if="files.length && files[0]" class="preview">
				<img :src="files[0]" :style="dynamicStyle" />
				<span class="clear" @click="onNativeClear" :style="dynamicStyle">
					<app-icon
						class="clear-icon"
						icon="fa fa-times"
						:size="imageIconSize"
						:color="imageDark ? 'white' : 'black'"
					/>
				</span>
			</span>
			<span v-else-if="imagePlaceholder && !imagePlaceholderCleared" class="preview">
				<img :src="imagePlaceholder" :style="dynamicStyle" />
				<span class="clear" @click="onPlaceholderClear" :style="dynamicStyle">
					<app-icon
						class="clear-icon"
						icon="fa fa-times"
						:size="imageIconSize"
						:color="imageDark ? 'white' : 'black'"
					/>
				</span>
			</span>
			<div v-else class="no-image" @click="$refs.file.click()" :style="dynamicStyle">
				<app-icon
					icon="fa fa-upload"
					:size="imageIconSize"
					:color="imageDark ? 'white' : 'black'"
				/>
			</div>
		</div>
		<v-file-input
			v-if="!image && !button"
			:label="label"
			:multiple="multiple"
			:accept="accept"
			@change="onChange"
			@click:clear="onClear"
			:prepend-icon="icon"
		/>
		<input
			ref="file"
			type="file"
			class="input-file-hidden"
			:multiple="multiple"
			:accept="accept"
			@change="onNativeChange($event.target.files)"
		/>
	</span>
</template>

<script>
import csvtojson from 'csvtojson'

export default {
	props: {
		label: String,
		multiple: Boolean,
		button: Boolean,
		image: Boolean,
		imageWidth: {
			type: [String, Number],
			default: 64,
		},
		imageHeight: {
			type: [String, Number],
			default: 64,
		},
		imagePlaceholder: {
			type: String,
			default: undefined,
		},
		imageDark: Boolean,
		tile: Boolean,
		outlined: Boolean,
		small: Boolean,
		large: Boolean,
		table: Boolean,
		buttonColor: {
			type: String,
			default: 'secondary',
		},
		icon: {
			type: String,
			default: 'fa-file-upload',
		},
		dataUrl: Boolean,
		type: {
			type: String,
			default: 'text',
			validator: val => ['text', 'image', 'json', 'csv', 'all'].includes(val),
		},
	},
	data() {
		return {
			files: [],
			imagePlaceholderCleared: false,
			total: 0,
			loading: false,
		}
	},
	computed: {
		accept() {
			switch (this.type) {
			case 'image':
				return 'image/*'
			case 'audio':
				return 'audio/*'
			case 'video':
				return 'video/*'
			case 'json':
				return 'application/json'
			case 'csv':
				return '.csv'
			case 'all':
				return '*'
			default:
				return 'text/*'
			}
		},
		readerMethod() {
			if (this.dataUrl) { return 'readAsDataURL' }

			switch (this.type) {
			case 'image':
			case 'audio':
			case 'video':
			case 'all':
				return 'readAsDataURL'
			default:
				return 'readAsText'
			}
		},
		imageIconSize() {
			const imageWidth = parseInt(this.imageWidth, 10)
			const imageHeight = parseInt(this.imageHeight, 10)
			return Math.round((
				imageWidth < imageHeight ? imageWidth : imageHeight
			) / 3)
		},
		dynamicStyle() {
			return {
				'--image-width': `${this.imageWidth}px`,
				'--image-height': `${this.imageHeight}px`,
				'--image-icon-size': `${this.imageIconSize}px`,
			}
		},
	},
	methods: {
		async formatContent(content) {
			switch (this.type) {
			case 'json':
				return JSON.parse(content)
			case 'csv':
				return csvtojson({
					delimiter: 'auto',
				}).fromString(content)
			default:
				return content
			}
		},
		async handleResult(content) {
			const res = await this.formatContent(content)
			this.files.push(res)

			if (this.files.length === this.total) {
				this.loading = false
				this.$emit('load', this.multiple ? this.files : this.files[0])

				// Reset values to upload same document if needed
				if (this.button) {
					this.$refs.file.value = null
				}
			}
		},
		onChange(files) {
			this.loading = true
			this.files = []
			this.total = files.length
			const reader = new FileReader()
			reader.onload = e => this.handleResult(e.target.result)
			files.forEach((file) => {
				reader[this.readerMethod](file)
			})
		},
		onNativeChange(files) {
			if (!files) { return }
			this.onChange([...files])
		},
		onClear() {
			this.$emit('load', null)
		},
		onNativeClear() {
			this.$refs.file.value = null
			this.files = []
			this.onClear()
		},
		onPlaceholderClear() {
			this.imagePlaceholderCleared = true
		},
	},
}
</script>

<style scoped>
	.input-file-wrapper .input-file-hidden {
		position: absolute;
		top: 0;
		left: 0;
		z-index: -1;
		opacity: 0;
	}

	.input-file-wrapper .dropper .preview {
		position: relative;
		display: inline-block;
	}

	.input-file-wrapper .dropper.dropperDark .preview {
		background: #111;
	}

	.input-file-wrapper .dropper .clear {
		position: absolute;
		right: 0;
		top: 0;
		left: 0;
		bottom: 0;
		width: var(--image-width);
		height: var(--image-height);
		text-align: center;
		padding-top: var(--image-icon-size);
		cursor:pointer;

		transition: background-color 100ms;
	}

	.input-file-wrapper .dropper .clear .clear-icon {
		opacity: 0;
		transition: opacity 100ms;
	}

	.input-file-wrapper .dropper .clear:hover {
		background: rgba(255,255,255,0.3);
	}

	.input-file-wrapper .dropper.dropperDark .clear:hover {
		background: rgba(0,0,0,0.5);
	}

	.input-file-wrapper .dropper .clear:hover .clear-icon {
		opacity: 1;
	}

	.input-file-wrapper .dropper img {
		width: var(--image-width);
		height: var(--image-height);
		object-fit: contain;
		display: inherit;
	}
	.input-file-wrapper .dropper .no-image {
		width: var(--image-width);
		height: var(--image-height);
		display: inline-block;
		border:1px solid #ccc;
		text-align: center;
		padding-top: var(--image-icon-size);
		border-radius: 2px;
		cursor: pointer;
	}

	.input-file-wrapper .dropper.dropperDark .no-image {
		background: #111;
	}
</style>
